/**
 * Cart Drawer Component v2.0.0
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * Self-contained cart drawer that extends ThemeDrawer.
 *
 * Attributes:
 * - data-auto-open-on-add: Automatically open when item is added to cart
 *
 * Usage:
 * <cart-drawer id="cart-drawer" data-direction="right" data-auto-open-on-add>
 *   <div data-drawer-overlay></div>
 *   <div data-drawer-content>
 *     Cart content here...
 *   </div>
 * </cart-drawer>
 *
 * ═══════════════════════════════════════════════════════════════════════════════
 */

import { ThemeDrawer } from './component-drawer.js';

// Get Logger from window.Theme
const Logger = window.Theme?.Logger || {
  ns: () => ({
    debug: () => {},
    info: () => {},
    warn: console.warn.bind(console),
    error: console.error.bind(console)
  })
};

const log = Logger.ns('cart-drawer');

// Get events from window.Theme
const ThemeEvents = window.Theme?.ThemeEvents || {
  cartUpdate: 'cart:update',
  cartError: 'cart:error'
};

export class CartDrawer extends ThemeDrawer {
  connectedCallback() {
    super.connectedCallback();

    // Self-contained: listen to events, react based on attributes
    this.#setupAutoOpenOnAdd();

    // Initialize Alpine on children - custom elements upgrade after Alpine.start()
    // so Alpine may have skipped our children during initial DOM walk
    if (typeof Alpine !== 'undefined' && Alpine.initTree) {
      Alpine.initTree(this);
      log.debug('Alpine.initTree called');
    }

    log.debug('Connected', {
      autoOpenOnAdd: this.hasAttribute('data-auto-open-on-add')
    });
  }

  /**
   * If data-auto-open-on-add is present, open drawer when cart:update fires with action='add'
   */
  #setupAutoOpenOnAdd() {
    if (!this.hasAttribute('data-auto-open-on-add')) return;

    document.addEventListener(ThemeEvents.cartUpdate, (e) => {
      if (e.detail?.data?.action === 'add') {
        log.debug('Auto-opening after add');
        this.open();
      }
    }, this.signal);
  }
}

// Register component
if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
  log.info('Registered');
}
